
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SystemStats {
  totalUsers: number;
  totalOrganizations: number;
  totalCities: number;
  totalAircraftTypes: number;
  totalFlights: number;
  loading: boolean;
  error: string | null;
}

export function useSystemStats() {
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    totalOrganizations: 0,
    totalCities: 0,
    totalAircraftTypes: 0,
    totalFlights: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStats(prev => ({ ...prev, loading: true, error: null }));

        // Fetch all stats in parallel
        const [
          usersResult,
          orgsResult,
          citiesResult,
          aircraftResult,
          flightsResult
        ] = await Promise.all([
          supabase.from('profiles').select('id', { count: 'exact', head: true }),
          supabase.from('organizations').select('id', { count: 'exact', head: true }),
          supabase.from('cities').select('id', { count: 'exact', head: true }),
          supabase.from('aircraft_types').select('id', { count: 'exact', head: true }),
          supabase.from('flights').select('id', { count: 'exact', head: true })
        ]);

        // Check for errors
        const errors = [usersResult.error, orgsResult.error, citiesResult.error, aircraftResult.error, flightsResult.error].filter(Boolean);
        if (errors.length > 0) {
          throw new Error(`Failed to fetch stats: ${errors.map(e => e?.message).join(', ')}`);
        }

        setStats({
          totalUsers: usersResult.count || 0,
          totalOrganizations: orgsResult.count || 0,
          totalCities: citiesResult.count || 0,
          totalAircraftTypes: aircraftResult.count || 0,
          totalFlights: flightsResult.count || 0,
          loading: false,
          error: null
        });

      } catch (error) {
        console.error('Error fetching system stats:', error);
        setStats(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch stats'
        }));
      }
    };

    fetchStats();
  }, []);

  return stats;
}
